import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Car, MapPin, X } from "lucide-react";

/**
 * RiderEtaWidget
 * ──────────────
 * Floating bubble on the customer dashboard that appears when the driver
 * of an active reserved ride taps "Start driving". Listens for the WS
 * `driver_started` push and shows a live-ticking countdown to arrival.
 * Auto-hides when the ETA reaches 0 or the rider dismisses.
 */
export default function RiderEtaWidget() {
  const { t } = useTranslation();
  const [state, setState] = useState(null); // {order_id, driver_name, target_ms}

  useEffect(() => {
    let ws = null;
    try {
      const token = localStorage.getItem("haulhub_token");
      if (!token) return;
      const wsUrl =
        (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws") +
        `/api/ws/dispatch?token=${encodeURIComponent(token)}`;
      ws = new WebSocket(wsUrl);
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data);
          if (m?.type === "driver_started" && m.order_id && m.eta_min) {
            setState({
              order_id: m.order_id,
              driver_name: m.driver_name,
              target_ms: Date.now() + m.eta_min * 60 * 1000,
            });
          }
        } catch (_) {}
      };
    } catch (_) {}
    return () => { if (ws) try { ws.close(); } catch (_) {} };
  }, []);

  // Second-by-second tick so the countdown feels alive.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!state) return;
    const id = setInterval(() => setTick((n) => n + 1), 30000); // update every 30s
    return () => clearInterval(id);
  }, [state]);

  if (!state) return null;
  const secsLeft = Math.max(0, Math.round((state.target_ms - Date.now()) / 1000));
  const minsLeft = Math.max(0, Math.round(secsLeft / 60));
  const hidden = secsLeft === 0;

  return (
    <div
      className={`fixed bottom-6 right-6 sm:right-8 z-[70] transition-all ${hidden ? "opacity-0 pointer-events-none translate-y-4" : ""}`}
      data-testid="rider-eta-widget"
    >
      <div className="relative rounded-2xl bg-electric-teal text-obsidian px-5 py-3 shadow-[0_16px_48px_-8px_rgba(0,229,255,0.4)] pr-10">
        <button
          onClick={() => setState(null)}
          className="absolute top-1 right-1 w-6 h-6 rounded-full hover:bg-black/10 flex items-center justify-center"
          aria-label="Dismiss"
          data-testid="rider-eta-widget-dismiss"
        >
          <X size={12} />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-obsidian/10 flex items-center justify-center animate-pulse">
            <Car size={18} strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-black opacity-70">
              {t("etaWidget.eyebrow", "Your driver is on the way")}
            </div>
            <div className="text-lg font-black leading-tight tabular-nums" data-testid="rider-eta-widget-min">
              {t("etaWidget.arrivesIn", "Arrives in {{n}} min", { n: minsLeft })}
            </div>
            {state.driver_name && (
              <div className="text-[11px] opacity-75 inline-flex items-center gap-1 mt-0.5">
                <MapPin size={10} /> {state.driver_name}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
